import React from 'react';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Briefcase, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>Careers | Skyreti EduCore</title>
  <meta
    name="description"
    content="Meet the founder of Skyreti EduCore and learn about our mission."
  />
</Helmet>

const Careers = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <Briefcase size={32} className="text-black" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Build your career while building amazing digital experiences for clients around the world
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-serif text-4xl font-bold text-black text-center mb-12">
            Why Skyretieducore?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-card-lime border-none">
              <CardHeader>
                <CardTitle className="font-serif text-2xl">Remote-First</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Work from anywhere in the world with flexible hours and unlimited PTO.</p>
              </CardContent>
            </Card>
            <Card className="bg-card-lime border-none">
              <CardHeader>
                <CardTitle className="font-serif text-2xl">Growth Focused</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Annual learning budget, conference attendance, and mentorship programs.</p>
              </CardContent>
            </Card>
            <Card className="bg-card-lime border-none">
              <CardHeader>
                <CardTitle className="font-serif text-2xl">Impactful Work</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Work on projects that make a real difference in education and business.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-serif text-4xl font-bold text-black mb-12">
            Open Positions
          </h2>
          <div className="space-y-6">
            {mockData.careers.map(job => (
              <Card key={job.id} className="bg-white border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl font-bold text-black mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-3 mb-4">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Briefcase size={14} />
                          {job.department}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <MapPin size={14} />
                          {job.location}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock size={14} />
                          {job.type}
                        </Badge>
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">
                        {job.description}
                      </p>
                      <div>
                        <h4 className="font-semibold text-black mb-2">Key Requirements:</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {job.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                 <Button
  className="bg-black text-white hover:bg-gray-800 md:self-start"
  asChild
>
  <Link to={`/join?role=${encodeURIComponent(job.title)}`}>
    Apply Now <ArrowRight className="ml-2" size={16} />
  </Link>
</Button>

                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Perks & Benefits */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-serif text-4xl font-bold text-black text-center mb-12">
            Perks & Benefits
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Health Insurance", desc: "Comprehensive coverage for you and family" },
              { title: "401(k) Matching", desc: "Up to 6% company match" },
              { title: "Unlimited PTO", desc: "Take time when you need it" },
              { title: "$2,000 Learning Budget", desc: "Annual professional development" },
              { title: "Latest Equipment", desc: "MacBook Pro and accessories" },
              { title: "Home Office Stipend", desc: "$500 for your workspace" },
              { title: "Team Retreats", desc: "Annual company offsite" },
              { title: "Stock Options", desc: "Own a piece of the company" }
            ].map((perk, index) => (
              <div key={index} className="bg-card-lime p-6 rounded-lg">
                <h3 className="font-semibold text-black mb-2">{perk.title}</h3>
                <p className="text-sm text-gray-700">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-serif text-4xl font-bold text-black text-center mb-12">
            Our Hiring Process
          </h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Apply", desc: "Submit your application through our contact form with your resume and portfolio" },
              { step: 2, title: "Initial Call", desc: "30-minute chat with our hiring team to learn about you and answer questions" },
              { step: 3, title: "Technical Interview", desc: "Role-specific assessment or portfolio review (1-2 hours)" },
              { step: 4, title: "Team Meet", desc: "Meet potential teammates and discuss collaboration style" },
              { step: 5, title: "Offer", desc: "We make you an offer and celebrate your joining the team!" }
            ].map(stage => (
              <div key={stage.step} className="flex gap-6 bg-white p-6 rounded-lg">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">{stage.step}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl font-bold text-black mb-2">{stage.title}</h3>
                  <p className="text-gray-700">{stage.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-4xl font-bold text-black mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            We're always looking for talented people. Send us your resume and let's talk!
          </p>
          <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg" asChild>
  <Link to="/join">Get in Touch</Link>
</Button>

        </div>
      </section>
    </div>
  );
};

export default Careers;
